/**
 * Request-scoped logging context.
 *
 * On the server the context is held in an AsyncLocalStorage, so it follows a
 * request through every await without being passed as an argument. In the
 * browser bundle 'async_hooks' resolves to nothing (see next.config.js) and a
 * single module-level store is used instead - a tab is single-threaded, so
 * there is nothing to isolate.
 */

let storage = null;

try {
  const { AsyncLocalStorage } = require("async_hooks");

  storage = new AsyncLocalStorage();
} catch (error) {
  storage = null;
}

let browserStore = {};

function get() {
  if (!storage) {
    return browserStore;
  }

  const store = storage.getStore();

  if (!store) {
    return null;
  }

  return store;
}

function run(context, callback) {
  if (!storage) {
    browserStore = { ...context };

    return callback();
  }

  // The stored value is mutable so middleware running later in the request
  // can enrich the context of an in-flight request.
  return storage.run({ ...context }, callback);
}

function set(partialContext) {
  if (!storage) {
    browserStore = { ...browserStore, ...partialContext };

    return;
  }

  const store = storage.getStore();

  if (!store) {
    return;
  }

  Object.assign(store, partialContext);
}

/**
 * Opens a context for everything that follows in this async resource, rather
 * than for the body of a callback. Meant for a one-shot process entry point
 * (the CLI), where there is no call to wrap - never for a server, where a
 * context that outlives its request would bleed into the next one.
 */
function enterWith(context) {
  if (!storage) {
    browserStore = { ...context };

    return;
  }

  storage.enterWith({ ...context });
}

function child(partialContext, callback) {
  if (!storage) {
    return callback();
  }

  const store = storage.getStore();

  return storage.run({ ...store, ...partialContext }, callback);
}

module.exports = { get, run, set, child, enterWith };
